import { atom } from 'jotai';
import type { MapRef } from 'react-map-gl';
import { getMapDefaultConfig } from 'components/map/atoms';
import type { MapUserConfig, MapUserConfigAtom } from 'components/map/types';
import { atomWithStorage } from 'jotai/utils';
import { DETAIL_CARDS } from './detail/section-cards';

// Client-side UI state for the Well Workspace feature. Server state lives in React
// Query — see `hooks.ts` for the reads and writes, `api.ts` for the requests, and
// `query-keys.ts` for the cache keys.

/**
 * Which section cards are visible (Layout settings) — every Story-2 section on by
 * default.
 *
 * Persisted per Story 2: the choice describes how this reader wants the workspace laid
 * out, not the workspace, so it is kept per browser rather than per workspace. A
 * missing or corrupt key falls back to the default above.
 */
export const visibleCardsAtom = atomWithStorage<Record<string, boolean>>(
  'well-workspace-visible-cards',
  Object.fromEntries(DETAIL_CARDS.map((card) => [card.id, true])),
  undefined,
  { getOnInit: true }
);

/**
 * Everything off. This is a picker for the sticks the reader has chosen — wells, units,
 * basins and title assets would only be noise over a handful of laterals, and the ambient
 * layers would make the endpoint markers hard to hit.
 */
const baseMapConfig = getMapDefaultConfig();
export const pickerMapConfigAtom: MapUserConfigAtom = atom<MapUserConfig>({
  ...baseMapConfig,
  wellsV2: { ...baseMapConfig.wellsV2, checked: false },
  // `checked: false` alone is not enough — Map renders DevelopmentAreasLayer
  // unconditionally and the layer tests its own options, so they have to be cleared.
  developmentAreas: {
    checked: false,
    options: {
      horizontalUnits: false,
      verticalUnits: false,
      fieldUnits: false,
      pricing: false,
    },
  },
});

/**
 * The Reconcile Wells inset: a read-only map where every ambient layer is off so only
 * the candidate wellbores draw, and scroll-zoom is off so the wheel keeps scrolling the
 * sheet instead of the map. `wells` is the one layer left on — WellMapLayer reads these
 * options to decide what to draw for each bore.
 */
export const reconcileMapConfigAtom: MapUserConfigAtom = atom<MapUserConfig>({
  ...baseMapConfig,
  wells: {
    checked: true,
    options: {
      surfaceHole: true,
      bottomHole: true,
      stick: true,
      survey: true,
      vertical: false,
    },
  },
  wellsV2: { ...baseMapConfig.wellsV2, checked: false },
  // `checked: false` alone is not enough: `Map` renders the development-areas layer
  // unconditionally and the layer tests only the individual options, so every unit in
  // view would still draw.
  developmentAreas: {
    checked: false,
    options: {
      horizontalUnits: false,
      verticalUnits: false,
      fieldUnits: false,
      pricing: false,
    },
  },
  basinTier: { ...baseMapConfig.basinTier, checked: false },
  titleEntityAssets: { ...baseMapConfig.titleEntityAssets, checked: false },
  scrollZoom: { checked: false },
  popups: { checked: false },
});

/**
 * The reconcile inset's own ref atom: the workspace page owns the shared one, and two
 * live maps writing the same ref leaves whichever unmounts last holding a dead handle.
 */
export const reconcileMapRefAtom = atom<MapRef | null>(null);

/**
 * Wells reconciled into a survivor during this session.
 *
 * A reconcile writes `deduplicated_into_uwi` and then enqueues the Elasticsearch
 * update, so for as long as that job is in flight the ES-backed wells listing keeps
 * returning the record it just retired. The listing filters against this set so the row
 * leaves the table on commit rather than on the next index.
 *
 * Session-scoped on purpose: it is a cover for a delay, not a store of truth. A reload
 * drops it, by which time the index has caught up and the server answers correctly on
 * its own.
 */
export const reconciledUwisAtom = atom<ReadonlySet<string>>(new Set<string>());

/** Adds the wells a reconcile just retired. */
export const addReconciledUwisAtom = atom(
  null,
  (get, set, uwis: readonly string[]) => {
    if (uwis.length === 0) return;
    set(reconciledUwisAtom, new Set([...get(reconciledUwisAtom), ...uwis]));
  }
);
